import React, { useEffect, useMemo } from 'react';
import { Button } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppstoreOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
  MailOutlined,
  NumberOutlined,
  PaperClipOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  TagsOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useDirectOrder } from '../../context/DirectOrderContext';
import { canUserApprove, getOrderGroup } from '../../utils/directOrderStatus';
import { isSystemAdmin } from './admin/ApproversPanel';

// The index loads the lists behind its counts once per signed-in user; pages
// refresh their own data as they need it.
let requestedFor = null;

/**
 * The left-hand index of the Direct Orders section: the section name, the
 * "New direct order" action, and every place you can go, each with a live
 * count. It replaces the old tab row and summary cards.
 */
const DOIndex = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isApprover,
    isAdmin,
    userDirectOrders,
    ccDirectOrders,
    allDirectOrders,
    missingAttachments,
    loadUserDirectOrders,
    loadCCDirectOrders,
    loadAllDirectOrders,
    loadMissingAttachments,
  } = useDirectOrder();

  const isDOAdmin = Boolean(user?.isDirectOrderAdmin);
  const canReview = isDOAdmin && (isAdmin || isApprover);

  useEffect(() => {
    if (!user?.email || requestedFor === user.email) return;
    requestedFor = user.email;
    loadUserDirectOrders();
    loadCCDirectOrders();
    loadMissingAttachments();
  }, [user?.email, loadUserDirectOrders, loadCCDirectOrders, loadMissingAttachments]);

  useEffect(() => {
    if (canReview && allDirectOrders.length === 0) {
      loadAllDirectOrders();
    }
    // only once the user's role is known
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReview]);

  const counts = useMemo(() => {
    const mine = { pending: 0, approved: 0, rejected: 0 };
    userDirectOrders.forEach((o) => {
      mine[getOrderGroup(o)] += 1;
    });
    const all = { pending: 0, approved: 0, rejected: 0 };
    allDirectOrders.forEach((o) => {
      all[getOrderGroup(o)] += 1;
    });
    return {
      mine,
      all,
      forYou: canReview ? allDirectOrders.filter((o) => canUserApprove(o, user?.email)).length : 0,
      cc: Array.isArray(ccDirectOrders) ? ccDirectOrders.length : 0,
    };
  }, [userDirectOrders, allDirectOrders, ccDirectOrders, canReview, user?.email]);

  const here = `${location.pathname}${location.search}`;

  const groups = [
    {
      label: 'Needs you',
      items: [
        ...(canReview
          ? [{ to: '/direct-order/dashboard?view=pending', icon: <AuditOutlined />, label: 'For your approval', count: counts.forYou, tone: 'accent' }]
          : []),
        { to: '/direct-order/view?status=rejected', icon: <CloseCircleOutlined />, label: 'Rejected', count: counts.mine.rejected, tone: 'bad' },
        { to: '/direct-order#todo', icon: <PaperClipOutlined />, label: 'Missing files', count: missingAttachments.length, tone: 'warn' },
      ],
    },
    {
      label: 'Browse',
      items: [
        { to: '/direct-order', icon: <AppstoreOutlined />, label: 'Overview' },
        { to: '/direct-order/view', icon: <FileTextOutlined />, label: 'My orders', count: userDirectOrders.length },
        { to: '/direct-order/view?status=pending', icon: <ClockCircleOutlined />, label: 'In approval', count: counts.mine.pending },
        { to: '/direct-order/cc-orders', icon: <MailOutlined />, label: "CC'd to me", count: counts.cc },
      ],
    },
    ...(isDOAdmin
      ? [
          {
            label: 'Admin',
            items: [
              { to: '/direct-order/dashboard', icon: <AuditOutlined />, label: 'Admin overview' },
              { to: '/direct-order/dashboard?view=pending', icon: <ClockCircleOutlined />, label: 'Pending', count: counts.all.pending },
              { to: '/direct-order/dashboard?view=approved', icon: <CheckCircleOutlined />, label: 'Approved', count: counts.all.approved },
              { to: '/direct-order/dashboard?view=rejected', icon: <CloseCircleOutlined />, label: 'Rejected', count: counts.all.rejected },
              { to: '/direct-order/dashboard?view=all', icon: <UnorderedListOutlined />, label: 'All orders', count: allDirectOrders.length },
              { to: '/direct-order/dashboard?view=attachments', icon: <PaperClipOutlined />, label: 'Missing attachments' },
              { to: '/direct-order/dashboard?view=reference', icon: <NumberOutlined />, label: 'Reference number' },
              { to: '/direct-order/dashboard?view=export', icon: <DownloadOutlined />, label: 'Export to CSV' },
              { to: '/direct-order/dashboard?view=lists', icon: <TagsOutlined />, label: 'Initiatives and justifications' },
              ...(isAdmin || isApprover
                ? [{ to: '/direct-order/dashboard?view=delete', icon: <DeleteOutlined />, label: 'Delete an order' }]
                : []),
              ...(isSystemAdmin(user)
                ? [{ to: '/direct-order/dashboard?view=approvers', icon: <SafetyCertificateOutlined />, label: 'Approvers' }]
                : []),
            ],
          },
        ]
      : []),
  ];

  // "For your approval" and admin "Pending" open the same list; only the first is marked current.
  let marked = false;
  const isCurrent = (item) => {
    if (marked || item.to.includes('#')) return false;
    if (item.to === here) {
      marked = true;
      return true;
    }
    return false;
  };

  return (
    <aside className="do-index" aria-label="Direct orders">
      <div className="do-index__head">
        <Link to="/direct-order" className="do-index__name">Direct orders</Link>
        <Button type="primary" icon={<PlusOutlined />} className="do-index__new" onClick={() => navigate('/direct-order/create')}>
          New direct order
        </Button>
      </div>
      <nav className="do-index__nav">
        {groups.map((group) => (
          <div className="do-index__group" key={group.label}>
            <p className="do-index__label">{group.label}</p>
            <ul>
              {group.items.map((item) => {
                const current = isCurrent(item);
                const loud = item.tone && item.count > 0;
                return (
                  <li key={`${group.label}-${item.label}`}>
                    <Link
                      to={item.to}
                      className={`do-index__item${current ? ' is-current' : ''}${loud ? ` is-${item.tone}` : ''}`}
                      aria-current={current ? 'page' : undefined}
                    >
                      <span className="do-index__icon" aria-hidden="true">{item.icon}</span>
                      <span className="do-index__text">{item.label}</span>
                      {item.count !== undefined && <span className="do-index__count">{item.count}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default DOIndex;
